import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("pm_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Mark background/silent calls so they don't spam global toasts.
  return config;
});

// De-dup toasts so a burst of network errors doesn't stack a wall of red.
const _lastToasts = new Map();
const _toastOnce = (key, fn, ttlMs = 4000) => {
  const now = Date.now();
  if ((_lastToasts.get(key) || 0) + ttlMs > now) return;
  _lastToasts.set(key, now);
  fn();
};

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    const detail = err.response?.data?.detail || {};
    // Callers can opt out of the global toast on a per-request basis by
    // passing `{ silent: true }` in the axios config — used by polling
    // endpoints (billing meter, homework refresh) that shouldn't nag.
    const silent = err.config?.silent === true;

    if (status === 401) {
      localStorage.removeItem("pm_token");
      localStorage.removeItem("pm_user");
      if (!window.location.pathname.includes("/auth")) {
        window.location.href = "/auth";
      }
    } else if (status === 402) {
      // Out-of-quota — surface an inline toast with an "Upgrade" CTA
      if (!silent) {
        const reason = detail.reason || "You're out of AI credits. Upgrade for more.";
        _toastOnce("402", () => toast.error(reason, {
          action: {
            label: detail.error === "feature_locked" ? "See plans" : "Upgrade",
            onClick: () => { window.location.href = "/app/plans"; },
          },
          duration: 8000,
        }));
      }
    } else if (status === 404) {
      // Callers usually know how to handle 404 themselves (rendering an
      // empty-state placeholder). Only toast if this isn't a benign lookup.
      if (!silent && err.config?.showNotFoundToast) {
        _toastOnce("404", () => toast.error(
          typeof detail === "string" ? detail : "That content wasn't found."
        ));
      }
    } else if (status === 429) {
      if (!silent) {
        _toastOnce("429", () => toast.warning(
          "Too many requests — take a breath and try again in a moment."
        ));
      }
    } else if (status >= 500 && status < 600) {
      if (!silent) {
        _toastOnce("5xx", () => toast.error(
          "Our servers hiccupped. Please retry in a moment — nothing was lost.",
          {
            action: {
              label: "Retry",
              onClick: () => window.location.reload(),
            },
            duration: 8000,
          }
        ));
      }
    } else if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
      if (!silent) {
        _toastOnce("net", () => toast.error(
          "You appear to be offline. Reconnect and try again."
        ));
      }
    } else if (err.code === "ECONNABORTED") {
      // Request timed out (axios timeout or backend slow)
      if (!silent) {
        _toastOnce("timeout", () => toast.warning(
          "That took too long. Try again in a moment."
        ));
      }
    }
    return Promise.reject(err);
  }
);
